import React from 'react';
import { toast } from 'react-toastify';
import authService, { type AgentConfig, type Store as StoreType } from '../../../../services/authService';
import type { PosSetupSavePayload } from '../../../../types/features/storeDetails/posSetup';
import { formatLastSync, formatSeenTimestamp } from '../utils/storeDetailsOverviewUtils';

type UseStoreDetailsOverviewPosParams = {
    agentConfig: AgentConfig | null;
    onSilentStoreRefresh?: () => Promise<StoreType | null> | StoreType | null | void;
    posService: {
        created_at?: string;
        updated_at?: string;
    } | null;
    refreshAgentConfig: () => Promise<void>;
    store: StoreType | null;
};

export const useStoreDetailsOverviewPos = ({
    agentConfig,
    onSilentStoreRefresh,
    posService,
    refreshAgentConfig,
    store,
}: UseStoreDetailsOverviewPosParams) => {
    const [posEnabled, setPosEnabled] = React.useState<boolean>(true);
    const [posDialogOpen, setPosDialogOpen] = React.useState<boolean>(false);
    const [posSaving, setPosSaving] = React.useState<boolean>(false);

    const posLastSeenLabel = React.useMemo(
        () =>
            formatSeenTimestamp(store?.pos_seen) ||
            formatLastSync(posService?.created_at, posService?.updated_at) ||
            formatLastSync(agentConfig?.created_at, agentConfig?.updated_at) ||
            formatLastSync(store?.created_at, store?.updated_at) ||
            '—',
        [agentConfig?.created_at, agentConfig?.updated_at, posService?.created_at, posService?.updated_at, store?.created_at, store?.pos_seen, store?.updated_at],
    );

    const handleSavePosSetup = React.useCallback(async (payload: PosSetupSavePayload) => {
        if (!store?.id) {
            toast.error('Store ID is missing');
            return;
        }

        setPosSaving(true);
        try {
            await authService.updateStore(store.id, { pos_type: payload.posType });
            if (payload.agentPayload) {
                await authService.saveAgentConfig(store.id, payload.agentPayload);
            }
            await Promise.resolve(onSilentStoreRefresh?.());
            await refreshAgentConfig();
            setPosDialogOpen(false);
            toast.success('POS setup updated successfully');
        } catch (error) {
            console.error('Failed to save POS setup', error);
            toast.error('Failed to save POS setup. Please try again.');
        } finally {
            setPosSaving(false);
        }
    }, [onSilentStoreRefresh, refreshAgentConfig, store?.id]);

    return {
        handleSavePosSetup,
        posDialogOpen,
        posEnabled,
        posLastSeenLabel,
        posSaving,
        setPosDialogOpen,
        setPosEnabled,
    };
};
